import React, { useEffect, useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import styles from "../../DeviceHealth/FrequencyBucketTable.module.css";
import layerStyles from "./LayerLatencyTable.module.css";
import CustomModal from "../../../../../Shared/CustomModal";
import TablePagination from "../../DeviceHealth/TablePagination";
import { useLatencyContextData } from "../context/Context";
import useApiRows from "../useApiRows";
import { LATENCY_BUCKETS } from "./latencyBuckets";
import LayerMetricCells, { LayerLatencyHeader, displayValue } from "./LayerLatencyCells";
import { getLayerLatencyVehicleWise } from "../LatencyService";

export const LAYER_LATENCY_VINS_MODAL_ID = "LayerLatencyDepotVins";

const ROWS_PER_PAGE = 10; // fixed, not user-changeable
const FIRST_COLUMNS = ["VIN No.", "Reg No.", "Status"];
// no Actual % for VINs: buckets + Overall %
const COL_COUNT = FIRST_COLUMNS.length + 1 + LATENCY_BUCKETS.length;

const toOption = (value) => [{ label: value, value }];

/*
 * selection: { city, depot } — only that depot's VINs are listed
 */
export default function LayerLatencyVinPopover({ selection,isOn }) {
    const { GlobalFilter } = useLatencyContextData();
    const [currentPage, setCurrentPage] = useState(1);
    const { city, depot } = selection || {};

    // API 3: VINs of the selected depot
    const [vins, isLoading] = useApiRows(
        getLayerLatencyVehicleWise,
        { ...GlobalFilter, ignitionOn:isOn, city: toOption(city), depot: toOption(depot) },
        !depot
    );

    useEffect(() => setCurrentPage(1), [vins]);

    const totalItems = vins.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedVins = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return vins.slice(start, start + ROWS_PER_PAGE);
    }, [vins, safePage]);

    return (
        <CustomModal
            id={LAYER_LATENCY_VINS_MODAL_ID}
            title={depot ? `${depot} Depot · Layer-wise latency` : "Layer-wise latency"}
            size="modal-xl"
            header={true}
            closeBtnClass="btn ms_btn btn-default"
        >
            {depot && (
                <div className={styles.popoverBody}>
                    <div className={styles.popoverMeta}>
                        <span className={styles.metaChip}>
                            <MapPin size={11} />
                            {city} · {depot}
                        </span>
                        <span className={styles.metaCount}>
                            {isLoading ? "Loading..." : `${totalItems} VINs`}
                        </span>
                    </div>

                    <div className={styles.popoverTableWrapper}>
                        <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable} ${layerStyles.table}`}>
                            <colgroup>
                                <col className={layerStyles.colVin} />
                                <col className={layerStyles.colReg} />
                                <col className={layerStyles.colActive} />
                                {LATENCY_BUCKETS.map((b) => (
                                    <col key={b.field} className={layerStyles.colBucket} />
                                ))}
                                <col className={layerStyles.colOverall} />
                            </colgroup>

                            <LayerLatencyHeader firstColumns={FIRST_COLUMNS} layerStyles={layerStyles} showActualPct={false} />

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={COL_COUNT}>
                                            <div className={styles.loadingState}>
                                                <span className={styles.spinner} />
                                                <span>Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : totalItems === 0 ? (
                                    <tr>
                                        <td colSpan={COL_COUNT}>
                                            <div className={styles.loadingState}>No VINs found for this depot</div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedVins.map((v) => (
                                        <tr key={v.vin} className={styles.locationRow}>
                                            <td className={styles.cityCell}>
                                                <span className={styles.vinText} title={v.vin}>{displayValue(v.vin)}</span>
                                            </td>
                                            <td>{displayValue(v.regNumber)}</td>
                                            <td>
                                                <span className={`${layerStyles.activeTag} ${v.activeVin ? layerStyles.activeOn : layerStyles.activeOff}`}>
                                                    {v.activeVin ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <LayerMetricCells row={v} showActualPct={false} />
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isLoading && (
                        <TablePagination
                            page={safePage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            pageSize={ROWS_PER_PAGE}
                            onPageChange={setCurrentPage}
                            itemLabel="VINs"
                        />
                    )}
                </div>
            )}
        </CustomModal>
    );
}
